import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../constants/property';

const MAINTENANCE_VENDOR_URL = `${API_BASE_URL}/api/maintence`;
const MAINTENANCE_VENDOR_LIST_URL = `${API_BASE_URL}/api/maintence/Vendor`;

const VendorSettings = () => {
  const [vendorValue, setVendorValue] = useState('');
  const [vendorCode, setVendorCode] = useState('');
  const [savingVendor, setSavingVendor] = useState(false);
  const [vendorError, setVendorError] = useState('');

  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [listError, setListError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [vendorPendingDelete, setVendorPendingDelete] = useState(null);

  const fetchVendors = useCallback(async () => {
    setListError('');
    setLoadingVendors(true);
    try {
      const response = await fetch(MAINTENANCE_VENDOR_LIST_URL, {
        method: 'GET',
        mode: 'cors',
      });
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setVendors(Array.isArray(data) ? data : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Maintenance vendor list error:', err);
      setListError(err.message || 'Failed to load vendors.');
      setVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  }, []);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const onAddVendorApi = async (e) => {
    e.preventDefault();
    const value = vendorValue.trim();
    const code = vendorCode.trim() || value;
    setVendorError('');
    if (!value) {
      setVendorError('Vendor name (value) is required.');
      return;
    }

    const payload = {
      type: 'Vendor',
      value,
      code,
    };

    setSavingVendor(true);
    try {
      const response = await fetch(MAINTENANCE_VENDOR_URL, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}: ${response.statusText}`);
      }
      alert('Vendor added successfully.');
      setVendorValue('');
      setVendorCode('');
      await fetchVendors();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Maintenance vendor API error:', err);
      setVendorError(err.message || 'Failed to add vendor.');
    } finally {
      setSavingVendor(false);
    }
  };

  const requestDeleteVendor = (row) => {
    setVendorPendingDelete(row);
  };

  const cancelDeleteVendor = () => {
    setVendorPendingDelete(null);
  };

  const confirmDeleteVendor = async () => {
    const row = vendorPendingDelete;
    if (!row || row.id == null) {
      setVendorPendingDelete(null);
      return;
    }

    setDeletingId(row.id);
    try {
      const response = await fetch(`${MAINTENANCE_VENDOR_URL}/id/${row.id}`, {
        method: 'DELETE',
        mode: 'cors',
      });
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}: ${response.statusText}`);
      }
      await fetchVendors();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Delete vendor error:', err);
      alert(`Failed to delete vendor: ${err.message || 'Unknown error'}`);
    } finally {
      setDeletingId(null);
      setVendorPendingDelete(null);
    }
  };

  return (
    <div className="space-y-6 min-w-0">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Vendor Settings</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <form
          onSubmit={onAddVendorApi}
          className="lg:col-span-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4"
        >
        <h3 className="font-medium text-gray-900">Add vendor</h3>

        <div className="space-y-2">
          <label htmlFor="vendorValue" className="block text-sm font-medium text-gray-700">
            Vendor name <span className="text-red-500">*</span>
          </label>
          <input
            id="vendorValue"
            type="text"
            value={vendorValue}
            onChange={(e) => {
              setVendorValue(e.target.value);
              setVendorError('');
            }}
            placeholder="e.g. ARIHANT TRADERS"
            autoComplete="off"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="vendorCode" className="block text-sm font-medium text-gray-700">
            Code
          </label>
          <input
            id="vendorCode"
            type="text"
            value={vendorCode}
            onChange={(e) => {
              setVendorCode(e.target.value);
              setVendorError('');
            }}
            placeholder="e.g. ARIHANT TRADERS (optional — defaults to name)"
            autoComplete="off"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>

        {/* <div className="rounded-md bg-gray-50 border border-gray-200 p-4 text-xs font-mono whitespace-pre-wrap break-words">
          {JSON.stringify(
            {
              type: 'Vendor',
              value: vendorValue.trim() || '(required)',
              code: vendorCode.trim() || vendorValue.trim() || '(required)',
            },
            null,
            2
          )}
        </div> */}

        {vendorError && (
          <p className="text-sm text-red-600" role="alert">
            {vendorError}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={savingVendor}
            className={`px-4 py-2 rounded-md text-white text-sm font-medium ${
              savingVendor ? 'bg-teal-600/70 cursor-not-allowed' : 'hover:opacity-90'
            }`}
            style={{ backgroundColor: '#009688' }}
          >
            {savingVendor ? 'Saving...' : 'Add '}
          </button>
        </div>
      </form>

        <div className="lg:col-span-8 min-w-0 rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-medium text-gray-900">Registered vendors</h3>
            <button
              type="button"
              onClick={() => fetchVendors()}
              disabled={loadingVendors}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-60"
            >
              {loadingVendors ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {listError && (
            <p className="text-sm text-red-600" role="alert">
              {listError}
            </p>
          )}

          {loadingVendors && vendors.length === 0 && !listError ? (
            <p className="text-sm text-gray-500">Loading vendors…</p>
          ) : vendors.length === 0 ? (
            <p className="text-sm text-gray-500">No vendors yet. Use the Add vendor form.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Vendor name</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-700">Code</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-700 w-28">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {vendors.map((row) => (
                    <tr key={row.id ?? `${row.value}-${row.code}`}>
                      <td className="px-4 py-2 text-gray-900">{row.value ?? '—'}</td>
                      <td className="px-4 py-2 text-gray-700">{row.code ?? '—'}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => requestDeleteVendor(row)}
                          disabled={deletingId === row.id}
                          className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          {deletingId === row.id ? 'Deleting…' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {vendorPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={cancelDeleteVendor}
            aria-hidden="true"
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete vendor</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-gray-900">
                &quot;{vendorPendingDelete.value ?? vendorPendingDelete.code}&quot;
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={cancelDeleteVendor}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteVendor}
                disabled={deletingId === vendorPendingDelete.id}
                className="px-4 py-2 text-sm font-medium text-white rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-60"
              >
                {deletingId === vendorPendingDelete.id ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorSettings;
